import { PageHero } from "@/components/shared/PageHero";
import { CTASection } from "@/components/shared/CTASection";
import { ServiceAreaLinks } from "@/components/shared/ServiceAreaLinks";
import { Link } from "@/lib/router-compat";
import { Home, Wrench, Shield, ArrowRight } from "lucide-react";
import sidingImage1 from "@/assets/projects/siding-installation-1.webp";
import sidingImage2 from "@/assets/projects/residential-siding.webp";

const sidingServices = [
  {
    icon: Home,
    title: "Siding Replacement",
    description:
      "Complete siding replacement with vinyl, James Hardie fiber cement, and other premium materials.",
    link: "/services/siding-replacement",
    image: sidingImage1,
  },
  {
    icon: Wrench,
    title: "Siding Repair",
    description:
      "Expert repair services to fix storm damage, rot, cracks, and other siding issues.",
    link: "/services/siding-repair",
    image: sidingImage2,
  },
];

const benefits = [
  "James Hardie certified installers",
  "Wide selection of colors and styles",
  "Energy-efficient insulated options",
  "Lifetime warranty available",
  "Expert color matching for repairs",
  "20+ years of installation experience",
];

const Siding = () => {
  return (
    <>
      <main>
        <PageHero
          badge="Our Services"
          title="Siding Services"
          subtitle="Beautiful, durable siding solutions for Minnesota homes."
        />

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Complete Siding Solutions
              </h2>
              <p className="text-lg text-muted-foreground">
                Transform your home's appearance and improve its protection with
                quality siding from Cedar Creek Construction. We offer a variety
                of materials and styles to match your vision.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              {sidingServices.map((service, index) => (
                <Link
                  key={index}
                  to={service.link}
                  className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="aspect-[4/3]">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-primary rounded-lg">
                        <service.icon className="w-6 h-6 text-primary-foreground" />
                      </div>
                      <h3 className="text-2xl font-heading font-bold text-white">
                        {service.title}
                      </h3>
                    </div>
                    <p className="text-white/80 mb-4">{service.description}</p>
                    <div className="flex items-center gap-2 text-primary font-semibold group-hover:gap-3 transition-all">
                      Learn More <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="bg-muted/50 rounded-xl p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-6 h-6 text-primary" />
                    <span className="text-sm font-semibold text-primary uppercase tracking-wide">
                      Why Choose Us
                    </span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                    Quality Siding That Lasts
                  </h3>
                  <p className="text-muted-foreground">
                    We install siding built to withstand Minnesota's extreme
                    temperatures and weather. Our expert installation ensures
                    your siding looks great and performs for years.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {benefits.map((benefit, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 bg-background rounded-lg p-3"
                    >
                      <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                      <span className="text-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <ServiceAreaLinks />

        <CTASection />
      </main>
    </>
  );
};

export default Siding;
